// src/components/Profiles/EditProfile.js
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { UserContext } from '../../contexts/UserContext';
import '../../App.css';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  
  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
    website: '',
    discipline: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [message, setMessage] = useState('');

  // Design disciplines for the dropdown
  const disciplines = [
    'Architecture',
    'Interior Design',
    'Industrial Design',
    'Graphic Design',
    'Product Design',
    'Furniture Design',
    'Landscape Architecture',
    'Urban Planning',
    'Fashion Design',
    'Jewelry Design',
    'Ceramics',
    'Sculpture',
    'Fine Art',
    'Photography',
    'Illustration',
    'Textile Design',
    'Lighting Design',
    'Exhibition Design',
    'Set Design',
    'Art Direction',
    'Design Research',
    'Design Strategy',
    'Other'
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) {
        setFetchingData(false);
        return;
      }

      console.log('🔍 Fetching user data for UID:', user.uid);

      try {
        // Fetch from Firebase users collection using the UID
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          console.log('✅ Found Firebase user data:', userData);
          
          setFormData({
            displayName: userData.displayName || '',
            bio: userData.bio || '',
            website: userData.website || '',
            discipline: userData.discipline || ''
          });
        } else {
          console.log('❌ No Firebase document found for UID:', user.uid);
          // Fallback to UserContext data
          setFormData({
            displayName: user.displayName || '',
            bio: user.bio || '',
            website: user.website || '',
            discipline: user.discipline || ''
          });
        }
      } catch (error) {
        console.error('❌ Error fetching user data:', error);
        // Fallback to UserContext data
        setFormData({
          displayName: user.displayName || '',
          bio: user.bio || '',
          website: user.website || '',
          discipline: user.discipline || ''
        });
      } finally {
        setFetchingData(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;

    setLoading(true);
    setMessage('');

    try {
      console.log('💾 Saving to Firebase UID:', user.uid);
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        displayName: formData.displayName,
        bio: formData.bio,
        website: formData.website,
        discipline: formData.discipline,
        updatedAt: new Date()
      });

      setMessage('Profile updated successfully!');
      setTimeout(() => {
        navigate(`/profiles/${user.email.split('@')[0]}`);
      }, 1500);
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      setMessage('Error updating profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/profiles/${user?.email?.split('@')[0]}`);
  };

  if (!user) {
    return <div className="profile-page">Please log in to edit your profile.</div>;
  }

  if (fetchingData) {
    return (
      <div className="discover-container">
        <div className="discover-main">
          <div style={{
            maxWidth: '600px',
            margin: '2rem auto',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: '20.4px',
              height: '20.4px',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #0d47a1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            Loading your profile data...
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="discover-container">
      <div className="discover-main">
        <div style={{
          maxWidth: '600px',
          margin: '2rem auto',
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '8.16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}>
          <h1 style={{ marginBottom: '2rem', fontSize: '1.5rem', fontWeight: 600 }}>
            Edit Profile
          </h1>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="displayName" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: '#333'
              }}>
                Display Name
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                maxLength={50}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
                placeholder="Your display name"
              />
              <small style={{ color: '#666', fontSize: '0.8rem' }}>
                {formData.displayName.length}/50 characters
              </small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="discipline" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: '#333'
              }}>
                Discipline
              </label>
              <select
                id="discipline"
                name="discipline"
                value={formData.discipline}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select your discipline</option>
                {disciplines.map(discipline => (
                  <option key={discipline} value={discipline}>
                    {discipline}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="bio" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: '#333'
              }}>
                Bio
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                maxLength={150}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                placeholder="Tell people about yourself..."
              />
              <small style={{ color: '#666', fontSize: '0.8rem' }}>
                {formData.bio.length}/150 characters
              </small>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label htmlFor="website" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: '#333'
              }}>
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
                placeholder="https://your-website.com"
              />
            </div>

            {message && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                borderRadius: '8px',
                backgroundColor: message.includes('Error') ? '#fee' : '#efe',
                color: message.includes('Error') ? '#c33' : '#060',
                fontSize: '0.9rem'
              }}>
                {message}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#333',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || fetchingData}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: loading ? '#ccc' : '#0d47a1',
                  color: 'white',
                  fontSize: '1rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;